import React, { useEffect } from 'react';
import { styled } from '@uhg-abyss/web/tools/styled';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { V2Card } from '@uhg-abyss/web/ui/Card';
import { useCollapse } from '@uhg-abyss/web/hooks/useCollapse';
import { V2TextInput } from '@uhg-abyss/web/ui/TextInput';
import { V2SelectInput } from '@uhg-abyss/web/ui/SelectInput';
import { V2FileUpload } from '@uhg-abyss/web/ui/FileUpload';
import { V2Button } from '@uhg-abyss/web/ui/Button';
import { V2Alert } from '@uhg-abyss/web/ui/Alert';
import { useFormV2 } from '@uhg-abyss/web/hooks/useForm';
import { useTinRegistration } from '@src/state/tinRegistration';
import { STATE_LIST } from '@src/common/stateOptions';
import { MAX_ADDRESSES, ADDR_START_ID } from '../constants';

// ─── Constants ────────────────────────────────────────────────────────────────

const MAX_FILE_NAME_LENGTH = 54; // 50 chars + 4 for .pdf extension

const streetFormatter = (value) =>
  value.replace(/[&/\\#,+()$~%.'":*?<>{}]/g, '');

const cityFormatter = (value) =>
  value.replace(/[!@#$%^&*)({}<>,.\[\]+_`~=\\|';":/?0-9]/g, '');

const CITY_VALIDATORS = {
  required: 'City is required.',
  validate: {
    isValidCity: (value) =>
      !value.match(/[a-zA-Z]/g) ? 'Please enter a valid city name.' : null,
  },
};

const ZIP_VALIDATORS = {
  required: 'ZIP code is required.',
  validate: {
    isValidZip: (value) =>
      !/^\d{5}$/.test(value) ? 'Please enter a valid ZIP code.' : null,
  },
};

// ─── Styled Components ────────────────────────────────────────────────────────

const SectionText = styled('p', {
  margin: '8px 0 18px',
  fontSize: '16px',
  fontWeight: 600,
});

const CardHeader = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  cursor: 'pointer',
});

const CardTitle = styled('span', {
  fontSize: '16px',
  fontWeight: 700,
  color: '$primary1',
});

const HeaderActions = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

const FieldGroup = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  margin: '16px 4px 0',
});

const CityStateZipRow = styled('div', {
  display: 'flex',
  gap: '12px',
  alignItems: 'flex-start',
});

const CardsWrapper = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
});

const FlexColWide = styled('div', { flex: 2 });
const FlexColNarrow = styled('div', { flex: 1 });

// Stable references — avoids Stitches regenerating class names on every render.
const FILE_UPLOAD_CONTENT_WIDTH_CSS = { width: '100%', maxWidth: '380px' };
const FILE_UPLOAD_CSS = {
  'abyss-file-upload-drop-zone': FILE_UPLOAD_CONTENT_WIDTH_CSS,
  'abyss-file-input-file-list-list': FILE_UPLOAD_CONTENT_WIDTH_CSS,
  'abyss-alert-root': FILE_UPLOAD_CONTENT_WIDTH_CSS,
  'abyss-file-upload-file-list-item': { wordBreak: 'break-word' },
};
const DOC360_UPLOAD_ERROR_ALERT_CSS = {
  'abyss-alert-root': FILE_UPLOAD_CONTENT_WIDTH_CSS,
};
const ERROR_CARD_CSS = {
  'abyss-card-root': { borderColor: '$error1' },
};

const FileUploadV2 = ({ validators, ...rest }) => {
  return (
    <V2FileUpload
      css={FILE_UPLOAD_CSS}
      variant="minimal"
      fileTypes={{ 'application/pdf': ['.pdf'] }}
      validators={
        validators
          ? {
              ...validators,
              validate: {
                ...validators.validate,
                maxFiles: (files) => {
                  return files && files.length > 1
                    ? 'Maximum 1 file allowed.'
                    : true;
                },
                fileLength: (files) => {
                  return files.some(
                    (file) => file.name.length > MAX_FILE_NAME_LENGTH
                  )
                    ? 'File Name cannot exceed 50 characters in length.'
                    : true;
                },
                fileSize: (files) => {
                  return files.some((file) => file.size > 5 * 1024 * 1024)
                    ? 'File is larger than 5 MB.'
                    : true;
                },
              },
            }
          : {}
      }
      {...rest}
    />
  );
};

// ─── AddressCard ─────────────────────────────────────────────────────────────

const AddressCard = ({
  addrId,
  addrNumber,
  totalAddresses,
  onAddrRemove,
  servicingAddrError,
  doc360AddrUploadError,
  setDoc360UploadErrors,
  servicingAddrFileModel,
  addrValidationCount,
}) => {
  const { collapseProps, buttonProps, isOpen, setIsOpen } = useCollapse({
    defaultIsOpen: true,
  });

  useEffect(() => {
    if (servicingAddrError) {
      setIsOpen(true);
    }
  }, [servicingAddrError, addrValidationCount]);

  const { setDocumentId } = useTinRegistration.actions();

  return (
    <V2Card css={servicingAddrError && ERROR_CARD_CSS}>
      <CardHeader {...buttonProps}>
        <CardTitle>
          Address ({addrNumber} of {totalAddresses})
        </CardTitle>
        <HeaderActions>
          <IconSymbol
            icon="delete"
            size="24px"
            variant="outlined"
            onClick={() => onAddrRemove(addrId)}
          />
          <IconSymbol
            icon={isOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
            size="24px"
          />
        </HeaderActions>
      </CardHeader>

      <div {...collapseProps}>
        <FieldGroup>
          <V2TextInput
            label="Address line 1"
            model={`secondaryServicing[${addrId}].streetLine1`}
            validators={{ required: 'Address line 1 is required.' }}
            formatter={streetFormatter}
            maxLength={80}
            isClearable
            name={`secondaryServicing-${addrId}-street1`}
            autoComplete="none"
          />

          <V2TextInput
            label="Address line 2"
            model={`secondaryServicing[${addrId}].streetLine2`}
            formatter={streetFormatter}
            maxLength={80}
            isClearable
            name={`secondaryServicing-${addrId}-street2`}
            autoComplete="none"
          />

          <CityStateZipRow>
            <FlexColWide>
              <V2TextInput
                label="City"
                model={`secondaryServicing[${addrId}].city`}
                validators={CITY_VALIDATORS}
                formatter={cityFormatter}
                maxLength={80}
                isClearable
                name={`secondaryServicing-${addrId}-city`}
                autoComplete="none"
              />
            </FlexColWide>

            <FlexColWide>
              <V2SelectInput
                label="State"
                model={`secondaryServicing[${addrId}].state`}
                placeholder="Select One"
                isSearchable
                isClearable
                options={STATE_LIST}
                validators={{ required: 'State is required.' }}
                name={`secondaryServicing-${addrId}-state`}
                autoComplete="none"
              />
            </FlexColWide>

            <FlexColNarrow>
              <V2TextInput
                label="ZIP code"
                model={`secondaryServicing[${addrId}].zipCode`}
                mask="zip"
                validators={ZIP_VALIDATORS}
                isClearable
                name={`secondaryServicing-${addrId}-zip`}
                autoComplete="none"
              />
            </FlexColNarrow>
          </CityStateZipRow>

          <FileUploadV2
            model={servicingAddrFileModel}
            label="Proof of servicing address"
            subText="File must be a PDF, under 5MB, and cannot be password-protected."
            validators={{
              required: 'Proof of servicing address is required.',
            }}
            onChange={() => {
              setDocumentId({ id: servicingAddrFileModel, value: undefined });
              setDoc360UploadErrors((curr) => {
                const updated = { ...curr };
                delete updated[servicingAddrFileModel];
                return updated;
              });
            }}
          />
          {doc360AddrUploadError && (
            <V2Alert
              variant="error"
              dismissible={false}
              inline
              css={DOC360_UPLOAD_ERROR_ALERT_CSS}
            >
              {doc360AddrUploadError}
            </V2Alert>
          )}
        </FieldGroup>
      </div>
    </V2Card>
  );
};

// ─── UpdateInformation ────────────────────────────────────────────────────────

export const UpdateInformation = ({
  servicingAddresses,
  setServicingAddresses,
  servicingAddressErrors,
  doc360UploadErrors,
  setDoc360UploadErrors,
  addrValidationCount,
}) => {
  const abyssForm = useFormV2();
  const { setDocumentId } = useTinRegistration.actions();

  const isMaxReached = servicingAddresses.length >= MAX_ADDRESSES;

  // Eligible IDs start at ADDR_START_ID and fill the next MAX_ADDRESSES slots.
  // Always picks the lowest unused ID so deleting an earlier slot and re-adding
  // reuses that slot instead of skipping ahead.
  const handleAddrAdd = () => {
    setServicingAddresses((curr) => {
      const nextId = Array.from(
        { length: MAX_ADDRESSES },
        (_, i) => ADDR_START_ID + i
      ).find((id) => !curr.includes(id));
      if (nextId === undefined) return curr;
      return [...curr, nextId];
    });
  };

  const handleAddrRemove = (addrId) => {
    const abyssFormVals = abyssForm.getValues();
    delete abyssFormVals.secondaryServicing[addrId];
    abyssForm.reset({ ...abyssFormVals });

    setDocumentId({
      id: `secondaryServicing[${addrId}].file`,
      value: undefined,
    });

    setDoc360UploadErrors((curr) => {
      const updated = { ...curr };
      delete updated[`secondaryServicing[${addrId}].file`];
      return updated;
    });

    setServicingAddresses((curr) => curr.filter((id) => id !== addrId));
  };

  return (
    <>
      <V2Card>
        <h4>Update W-9 (optional)</h4>

        <SectionText>
          If you need to submit an updated IRS Form W-9, upload it below. Make
          sure it is dated within the last 12 months to avoid delays.
        </SectionText>

        <FileUploadV2
          model="w9.file"
          label="Upload your completed W-9"
          subText="Must be a PDF under 5MB and not password-protected."
          validators={{}}
          onChange={() => {
            setDocumentId({ id: 'w9.file', value: undefined });
            setDoc360UploadErrors((curr) => {
              const updated = { ...curr };
              delete updated['w9.file'];
              return updated;
            });
          }}
        />
        {doc360UploadErrors['w9.file'] && (
          <V2Alert
            variant="error"
            dismissible={false}
            inline
            css={DOC360_UPLOAD_ERROR_ALERT_CSS}
          >
            {doc360UploadErrors['w9.file']}
          </V2Alert>
        )}
      </V2Card>

      <V2Card>
        <Layout.Group alignLayout="between">
          <h4>Add additional servicing addresses (optional)</h4>

          <V2Button
            variant="text"
            color="brand"
            isDisabled={isMaxReached}
            icon={{
              icon: 'add_circle',
              position: 'trailing',
              variant: 'outlined',
            }}
            onClick={handleAddrAdd}
            size="$sm"
          >
            Add address
          </V2Button>
        </Layout.Group>

        <SectionText>
          Provide up to {MAX_ADDRESSES} additional servicing addresses you may
          have. All addresses used to submit claims are required.
        </SectionText>

        {servicingAddresses.length > 0 && (
          <CardsWrapper>
            {servicingAddresses.map((id, index) => (
              <AddressCard
                key={id}
                addrId={id}
                addrNumber={index + 1}
                totalAddresses={servicingAddresses.length}
                onAddrRemove={handleAddrRemove}
                servicingAddrError={servicingAddressErrors.has(id)}
                doc360AddrUploadError={
                  doc360UploadErrors[`secondaryServicing[${id}].file`]
                }
                setDoc360UploadErrors={setDoc360UploadErrors}
                servicingAddrFileModel={`secondaryServicing[${id}].file`}
                addrValidationCount={addrValidationCount}
              />
            ))}
          </CardsWrapper>
        )}
      </V2Card>
    </>
  );
};
